/**
 * Embedded Map for Network Graph Side Panel
 * Shows location data from graph connections (SMS, Calls, Dwells)
 * Filters by selected nodes
 * Supports area selection to filter the graph
 * 
 * DWELL FLOW MAP MODE - Visualizes traffic between dwell locations:
 * - Bezier curves showing movement between dwells
 * - Edge weight = frequency of travel between locations
 * - Hub detection for frequently visited locations
 * - Click location to highlight connected dwells
 */

import React, { useMemo, useRef, useEffect, useState, useCallback } from 'react';
import Map, { NavigationControl, ScaleControl, MapRef, Source, Layer } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { 
  Box, 
  Typography, 
  Paper, 
  Stack, 
  IconButton, 
  Tooltip, 
  useTheme,
  alpha,
  Switch,
  FormControlLabel,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CircleIcon from '@mui/icons-material/Circle';
import CropFreeIcon from '@mui/icons-material/CropFree';
import ClearIcon from '@mui/icons-material/Clear';
import TimelineIcon from '@mui/icons-material/Timeline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import type { MapLayerMouseEvent } from 'react-map-gl';
import MapStyleControl, { MapStyleType } from '@signals/cop-ui/components/MapStyleControl';

// Area bounds for filtering
export interface AreaBounds {
  minLng: number;
  minLat: number;
  maxLng: number;
  maxLat: number;
}

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || '';
const DEFAULT_CENTER: [number, number] = [-110.9747, 32.2226];
const DEFAULT_ZOOM = 11;

function toFiniteNumber(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string') {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function toValidLngLat(lng: unknown, lat: unknown): [number, number] | null {
  const lngNum = toFiniteNumber(lng);
  const latNum = toFiniteNumber(lat);
  if (lngNum === null || latNum === null) return null;
  return [lngNum, latNum];
}

interface Link {
  source: string | any;
  target: string | any;
  type?: string;
  weight?: number;
  timestamp?: string;
  latitude?: number;
  longitude?: number;
  location_name?: string;
  place_name?: string;
  // Optional endpoint coordinates for comms interactions
  source_latitude?: number;
  source_longitude?: number;
  target_latitude?: number;
  target_longitude?: number;
  source_place_name?: string | null;
}

interface Node {
  id: string;
  type?: string;
  label?: string;
  entity_type?: string;
  place_name?: string;
}

interface NetworkGraphMapProps {
  links: Link[];
  nodes?: Node[];
  selectedNodes: Set<string>;
  trackedEntities?: Set<string>; // Entities tracked by graph (includes fan-out from selected locations)
  onAreaSelect?: (bounds: AreaBounds | null) => void;
  selectedArea?: AreaBounds | null;
  showLegend?: boolean;
  onNodeClick?: (nodeId: string, connectedEntityIds?: string[], ctrlKey?: boolean) => void;
  onSelectionInfoChange?: (info: { label: string; type: string; connections: number } | null) => void;
  showPopup?: boolean;
  selectionResetNonce?: number;
  selectedConnectionTypes?: Set<string>; // If set, only these connection types are shown (pie chart)
  connectionType?: string; // Single filter from dropdown dialog (e.g., 'dwell', 'all', 'digital')
  // Flow map state - lifted to parent for sync with graph
  flowMapEnabled?: boolean;
  onFlowMapToggle?: (enabled: boolean) => void;
  // Zoom to a specific location on the map (triggered by parent)
  zoomToLocation?: { lat: number; lng: number; zoom?: number } | null;
}

// ============================================================================
// DWELL FLOW MAP UTILITIES
// ============================================================================

interface DwellNode {
  id: string;
  lat: number;
  lng: number;
  label: string;
  visitCount: number;      // How many times entities visited this location
  entityCount: number;     // How many unique entities visited
  entities: string[];      // List of entity IDs that visited
  totalDuration: number;   // Total dwell time at this location
  connectionCount: number; // Number of connected locations (edges)
  tier: 1 | 2 | 3;         // 1=Hub (always visible), 2=Secondary (expand/zoom), 3=Tertiary (deep zoom)
  connectedHubs: string[]; // Hub nodes this location is connected to (for expansion)
  pointCount: number;      // How many coordinate points were grouped into this location
}

interface DwellFlowEdge {
  id: string;
  sourceId: string;
  targetId: string;
  weight: number;          // How many times this transition occurred
  entityIds: string[];     // Which entities made this transition
}

/**
 * Generate a bezier curve between two points with optional bundling offset
 * Returns an array of [lng, lat] coordinates for a smooth curve
 */
function generateBezierCurve(
  start: [number, number],
  end: [number, number],
  bundleOffset: number = 0,
  numPoints: number = 32
): [number, number][] {
  const [startLng, startLat] = start;
  const [endLng, endLat] = end;
  
  const midLng = (startLng + endLng) / 2;
  const midLat = (startLat + endLat) / 2;
  
  const dx = endLng - startLng;
  const dy = endLat - startLat;
  const dist = Math.sqrt(dx * dx + dy * dy);
  
  if (dist < 0.0001) {
    // Same location - return straight line
    return [[startLng, startLat], [endLng, endLat]];
  }
  
  const perpX = -dy / dist;
  const perpY = dx / dist;
  
  // Curve amount proportional to distance with bundling offset
  const curveAmount = dist * 0.15 + bundleOffset * 0.0001;
  const controlLng = midLng + perpX * curveAmount;
  const controlLat = midLat + perpY * curveAmount;
  
  const points: [number, number][] = [];
  for (let i = 0; i <= numPoints; i++) {
    const t = i / numPoints;
    const oneMinusT = 1 - t;
    const lng = oneMinusT * oneMinusT * startLng +
      2 * oneMinusT * t * controlLng +
      t * t * endLng;
    const lat = oneMinusT * oneMinusT * startLat +
      2 * oneMinusT * t * controlLat +
      t * t * endLat;
    points.push([lng, lat]);
  }
  
  return points;
}

/**
 * Round coordinates to cluster nearby dwells
 */
function roundCoordinate(coord: number, precision: number = 4): number {
  const factor = Math.pow(10, precision);
  return Math.round(coord * factor) / factor;
}

/**
 * Clean place name for consistent grouping (lowercase, trim, remove special chars)
 */
function cleanPlaceName(name: string | undefined | null): string | null {
  if (!name || typeof name !== 'string') return null;
  const cleaned = name.trim().toLowerCase().replace(/[^\w\s]/g, '');
  return cleaned.length > 0 ? cleaned : null;
}

/**
 * Generate a unique location ID using smart grouping:
 * - Named places: group by name + coarse coords (~1km) to catch same place with GPS noise
 * - Unnamed: group by tight coords (~11m) 
 */
function dwellLocationId(lat: number, lng: number, placeName?: string): string {
  const cleanedName = cleanPlaceName(placeName);
  
  if (cleanedName) {
    // Named place: use coarse coords (2 decimals ≈ 1.1km) to group same-named nearby locations
    // but keep different locations with same name (e.g., different Walmarts) separate
    const coarseLat = roundCoordinate(lat, 2);
    const coarseLng = roundCoordinate(lng, 2);
    return `named_${cleanedName}_${coarseLat}_${coarseLng}`;
  } else {
    // Unnamed: use tight coords (4 decimals ≈ 11m) to group nearby points
    const tightLat = roundCoordinate(lat, 4);
    const tightLng = roundCoordinate(lng, 4);
    return `coord_${tightLat}_${tightLng}`;
  }
}

/**
 * Simple hash function for deterministic bundling offsets
 */
function hashString(input: string): number {
  let hash = 5381;
  for (let i = 0; i < input.length; i++) {
    hash = ((hash << 5) + hash) ^ input.charCodeAt(i);
  }
  return hash >>> 0;
}

/**
 * Extract coordinates from a graph location node ID
 * Matches formats like: "Location (33.4797, -112.0026)" or "coord_33.4797_-112.0026"
 */
function extractCoordsFromId(id: string): { lat: number; lng: number } | null {
  if (!id) return null;
  
  // Match "Location (lat, lng)" format from graph
  const locationMatch = id.match(/Location\s*\(\s*(-?\d+\.?\d*)\s*,\s*(-?\d+\.?\d*)\s*\)/i);
  if (locationMatch) {
    return { lat: parseFloat(locationMatch[1]), lng: parseFloat(locationMatch[2]) };
  }
  
  // Match "coord_lat_lng" format from flow map
  const coordMatch = id.match(/coord_(-?\d+\.?\d*)_(-?\d+\.?\d*)/);
  if (coordMatch) {
    return { lat: parseFloat(coordMatch[1]), lng: parseFloat(coordMatch[2]) };
  }
  
  // Match "named_name_lat_lng" format from flow map
  const namedMatch = id.match(/named_[^_]+_(-?\d+\.?\d*)_(-?\d+\.?\d*)/);
  if (namedMatch) {
    return { lat: parseFloat(namedMatch[1]), lng: parseFloat(namedMatch[2]) };
  }
  
  return null;
}

/**
 * Check if a flow node matches any selected location from the graph
 * Uses coordinate proximity matching since IDs may differ
 */
function isFlowNodeSelectedByGraph(
  nodeLat: number, 
  nodeLng: number, 
  selectedNodes: Set<string> | undefined,
  tolerance: number = 0.001 // ~110 meters at equator
): boolean {
  if (!selectedNodes || selectedNodes.size === 0) return false;
  
  let found = false;
  selectedNodes.forEach(selectedId => {
    if (found) return;
    const coords = extractCoordsFromId(selectedId);
    if (coords) {
      const latDiff = Math.abs(coords.lat - nodeLat);
      const lngDiff = Math.abs(coords.lng - nodeLng);
      if (latDiff <= tolerance && lngDiff <= tolerance) {
        found = true;
      }
    }
  });
  return found;
}

// ============================================================================
// COMPONENT
// ============================================================================

// Custom comparison function to avoid circular reference issues with links/nodes
// from force-directed graph (which modifies objects in place with circular refs)
const arePropsEqual = (
  prevProps: NetworkGraphMapProps,
  nextProps: NetworkGraphMapProps
): boolean => {
  // Compare by length for links and nodes (avoids circular reference comparison)
  if (prevProps.links.length !== nextProps.links.length) return false;
  if ((prevProps.nodes?.length ?? 0) !== (nextProps.nodes?.length ?? 0)) return false;
  
  // Compare selected nodes Set - must check both directions for proper equality
  if (prevProps.selectedNodes !== nextProps.selectedNodes) {
    // Different sizes = definitely changed
    if (prevProps.selectedNodes.size !== nextProps.selectedNodes.size) return false;
    // Same size but different contents - check both directions
    const prevIds = Array.from(prevProps.selectedNodes);
    const nextIds = Array.from(nextProps.selectedNodes);
    for (let i = 0; i < prevIds.length; i++) {
      if (!nextProps.selectedNodes.has(prevIds[i])) return false;
    }
    for (let i = 0; i < nextIds.length; i++) {
      if (!prevProps.selectedNodes.has(nextIds[i])) return false;
    }
  }
  
  // Simple equality for other props
  if (prevProps.selectedArea !== nextProps.selectedArea) return false;
  if (prevProps.showLegend !== nextProps.showLegend) return false;
  if (prevProps.onAreaSelect !== nextProps.onAreaSelect) return false;
  if (prevProps.onNodeClick !== nextProps.onNodeClick) return false;
  
  // Compare connection types filter
  if (prevProps.selectedConnectionTypes !== nextProps.selectedConnectionTypes) {
    if (!prevProps.selectedConnectionTypes || !nextProps.selectedConnectionTypes) return false;
    if (prevProps.selectedConnectionTypes.size !== nextProps.selectedConnectionTypes.size) return false;
    const prevTypes = Array.from(prevProps.selectedConnectionTypes);
    for (let i = 0; i < prevTypes.length; i++) {
      if (!nextProps.selectedConnectionTypes.has(prevTypes[i])) return false;
    }
  }
  
  // Compare connection type dropdown filter
  if (prevProps.connectionType !== nextProps.connectionType) return false;
  
  // Compare flow map state
  if (prevProps.flowMapEnabled !== nextProps.flowMapEnabled) return false;
  
  // Compare tracked entities (for graph sync)
  if (prevProps.trackedEntities !== nextProps.trackedEntities) {
    if (!prevProps.trackedEntities || !nextProps.trackedEntities) return false;
    if (prevProps.trackedEntities.size !== nextProps.trackedEntities.size) return false;
    const prevTracked = Array.from(prevProps.trackedEntities);
    for (let i = 0; i < prevTracked.length; i++) {
      if (!nextProps.trackedEntities.has(prevTracked[i])) return false;
    }
  }
  
  // Compare zoomToLocation (different object reference = new zoom request)
  if (prevProps.zoomToLocation !== nextProps.zoomToLocation) return false;
  
  return true;
};

const NetworkGraphMapInner: React.FC<NetworkGraphMapProps> = ({ 
  links, 
  nodes: _nodes = [],
  selectedNodes,
  trackedEntities, 
  onAreaSelect, 
  selectedArea, 
  showLegend = true,
  onNodeClick,
  onSelectionInfoChange: _onSelectionInfoChange,
  showPopup: _showPopup = true,
  selectionResetNonce: _selectionResetNonce,
  selectedConnectionTypes,
  connectionType: _connectionType,
  flowMapEnabled: _flowMapEnabledProp, // Temporarily unused - flow map disabled
  onFlowMapToggle,
  zoomToLocation,
}) => {
  const mapRef = useRef<MapRef | null>(null);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  
  // Map style based on theme
  const [mapStyle, setMapStyle] = useState<MapStyleType>(() => 
    theme.palette.mode === 'light' ? 'streets' : 'dark'
  );
  
  // Area selection state
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawStart, setDrawStart] = useState<{ lng: number; lat: number } | null>(null);
  const [drawEnd, setDrawEnd] = useState<{ lng: number; lat: number } | null>(null);
  
  // Dwell Flow Map state - controlled by parent if prop provided, otherwise internal
  // TEMPORARILY DISABLED for debugging - force flowMapEnabled to false
  const [_flowMapEnabledInternal, setFlowMapEnabledInternal] = useState(false);
  // const flowMapEnabled = _flowMapEnabledProp !== undefined ? _flowMapEnabledProp : _flowMapEnabledInternal;
  const flowMapEnabled = false; // TODO: Re-enable when flow map sync issues are resolved
  const setFlowMapEnabled = onFlowMapToggle || setFlowMapEnabledInternal;
  const [selectedFlowNode, setSelectedFlowNode] = useState<string | null>(null);
  const [flowPopupInfo, setFlowPopupInfo] = useState<{
    node: DwellNode;
    lng: number;
    lat: number;
  } | null>(null);
  
  // Hierarchical node expansion - track which hub nodes are expanded
  const [expandedHubs, setExpandedHubs] = useState<Set<string>>(new Set());
  
  // Track current zoom level for adaptive rendering
  const [currentZoom, setCurrentZoom] = useState(DEFAULT_ZOOM);
  
  // Legend/Info panel toggle - collapsed by default
  const [infoPanelOpen, setInfoPanelOpen] = useState(false);
  
  // Check if dwells are specifically selected (not "All Types")
  // Only show flow map option when user explicitly filters to dwells
  // Supports both: dropdown (connectionType) and pie chart (selectedConnectionTypes)
  const isDwellsOnlySelected = 
  _connectionType === 'dwell' || // Dropdown filter
    (selectedConnectionTypes !== undefined && 
      selectedConnectionTypes.size === 1 && 
      selectedConnectionTypes.has('dwell')); // Pie chart filter

  // Turn off flow map if user changes filter away from dwells-only
  useEffect(() => {
    if (!isDwellsOnlySelected && flowMapEnabled) {
      setFlowMapEnabled(false);
      setSelectedFlowNode(null);
      setFlowPopupInfo(null);
      if (onNodeClick) onNodeClick('', []);
    }
  }, [isDwellsOnlySelected, flowMapEnabled, onNodeClick]);

  // Clear local drawing state when parent clears selectedArea
  useEffect(() => {
    if (!selectedArea) {
      setDrawStart(null);
      setDrawEnd(null);
    }
  }, [selectedArea]);

  // Clear flow node selection when graph selection grows (new entities added from graph)
  // This ensures the map shows all graph-selected entity locations, not just the flow node's neighbors
  const prevSelectedSizeRef = useRef<number>(0);
  useEffect(() => {
    const currSize = selectedNodes?.size ?? 0;
    const prevSize = prevSelectedSizeRef.current;
    
    // If selection grew (user added more nodes from graph), clear flow node selection
    // to show all locations for ALL selected entities
    if (currSize > prevSize && selectedFlowNode) {
      setSelectedFlowNode(null);
      setFlowPopupInfo(null);
    }
    
    prevSelectedSizeRef.current = currSize;
  }, [selectedNodes?.size, selectedFlowNode]);

  // Resize map when container size changes (e.g., panel resize)
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!mapContainerRef.current) return;
    
    const resizeObserver = new ResizeObserver(() => {
      // Trigger map resize after a small delay to let layout settle
      setTimeout(() => {
        mapRef.current?.resize();
      }, 0);
    });
    
    resizeObserver.observe(mapContainerRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  // Handle zoom to location from parent (e.g., "Zoom to Node" from graph)
  useEffect(() => {
    if (zoomToLocation && mapRef.current) {
      mapRef.current.flyTo({
        center: [zoomToLocation.lng, zoomToLocation.lat],
        zoom: zoomToLocation.zoom ?? 14,
        duration: 1000,
      });
    }
  }, [zoomToLocation]);

  // Convert map style type to Mapbox style URL
  const getMapboxStyle = (style: MapStyleType): string => {
    const styles: Record<MapStyleType, string> = {
      streets: 'mapbox://styles/mapbox/streets-v12',
      satellite: 'mapbox://styles/mapbox/satellite-v9',
      'satellite-streets': 'mapbox://styles/mapbox/satellite-streets-v12',
      dark: 'mapbox://styles/mapbox/dark-v11',
      light: 'mapbox://styles/mapbox/light-v11',
      outdoors: 'mapbox://styles/mapbox/outdoors-v12',
    };
    return styles[style] || styles.dark;
  };

  // ============================================================================
  // FLOW MAP DATA PROCESSING
  // ============================================================================

  // Extract link data safely to avoid circular reference issues from force-directed graph
  // Use JSON serialization of primitive data to create stable keys
  const linksKey = useMemo(() => {
    try {
      // Create a stable key from essential primitive data only
      const essentialData = links.map(link => ({
        sourceId: typeof link.source === 'string' ? link.source :
                 (link.source && typeof link.source === 'object' ? (link.source as any).id || '' : ''),
        targetId: typeof link.target === 'string' ? link.target :
                 (link.target && typeof link.target === 'object' ? (link.target as any).id || '' : ''),
        type: link.type || 'unknown',
        weight: link.weight || 1,
        latitude: link.latitude,
        longitude: link.longitude,
        source_latitude: (link as any).source_latitude,
        source_longitude: (link as any).source_longitude,
        target_latitude: (link as any).target_latitude,
        target_longitude: (link as any).target_longitude,
        place_name: link.place_name || link.location_name || '',
      }));
      return JSON.stringify(essentialData);
    } catch (e) {
      // Fallback to length-based key if JSON serialization fails
      return `links-${links.length}-${links[0]?.type || ''}`;
    }
  }, [links]);

  // const nodesKey = useMemo(() => {
  //   try {
  //     const essentialData = nodes.map(node => ({
  //       id: node.id,
  //       label: node.label || node.id,
  //       place_name: node.place_name || '',
  //     }));
  //     return JSON.stringify(essentialData);
  //   } catch (e) {
  //     return `nodes-${nodes.length}-${nodes[0]?.id || ''}`;
  //   }
  // }, [nodes]);

  const safeLinks = useMemo(() => {
    const result: Array<{
      sourceId: string;
      targetId: string;
      type: string;
      weight: number;
      latitude: number | undefined;
      longitude: number | undefined;
      source_latitude?: number;
      source_longitude?: number;
      target_latitude?: number;
      target_longitude?: number;
      source_place_name?: string | null;
      place_name: string;
      timestamp?: string;
    }> = [];

    for (let i = 0; i < links.length; i++) {
      const link = links[i];
      // Access only primitive properties to avoid circular reference traversal
      const sourceId = typeof link.source === 'string' ? link.source :
                       (link.source && typeof link.source === 'object' ? (link.source as any).id || '' : '');
      const targetId = typeof link.target === 'string' ? link.target :
                       (link.target && typeof link.target === 'object' ? (link.target as any).id || '' : '');

      result.push({
        sourceId,
        targetId,
        type: link.type || 'unknown',
        weight: link.weight || 1,
        latitude: link.latitude,
        longitude: link.longitude,
        source_latitude: (link as any).source_latitude,
        source_longitude: (link as any).source_longitude,
        target_latitude: (link as any).target_latitude,
        target_longitude: (link as any).target_longitude,
        source_place_name: (link as any).source_place_name,
        place_name: link.place_name || link.location_name || '',
        timestamp: link.timestamp,
      });
    }
    return result;
  }, [linksKey]);

  // When the main graph has a selection, constrain the map to only the selected entities' links.
  const selectedNodesKey = useMemo(() => {
    if (!selectedNodes || selectedNodes.size === 0) return '';
    return Array.from(selectedNodes).sort().join('|');
  }, [selectedNodes]);

  // Key for tracked entities to detect changes
  const trackedEntitiesKey = useMemo(() => {
    if (!trackedEntities || trackedEntities.size === 0) return '';
    return Array.from(trackedEntities).sort().join('|');
  }, [trackedEntities]);

  const filteredSafeLinks = useMemo(() => {
    if (!selectedNodes || selectedNodes.size === 0) return safeLinks;

    // Use trackedEntities from graph if provided (this includes entities expanded from selected locations)
    // This ensures the map shows the same data as the graph's fan-out logic
    if (trackedEntities && trackedEntities.size > 0) {
      return safeLinks.filter((link) => {
        // Keep links that touch selected nodes OR tracked entities (fan-out from graph)
        return (
          selectedNodes.has(link.sourceId) ||
          selectedNodes.has(link.targetId) ||
          trackedEntities.has(link.sourceId) ||
          trackedEntities.has(link.targetId)
        );
      });
    }

    // Fallback: When trackedEntities not provided, use local dwell expansion logic
    // When a dwell location is selected, expand the selection to include all dwell
    // activity for the connected entities so we can derive movement to other locations.
    const dwellEntityIds = new Set<string>();
    safeLinks.forEach((link) => {
      if (link.type !== 'dwell') return;
      if (selectedNodes.has(link.sourceId) || selectedNodes.has(link.targetId)) {
        if (link.sourceId) dwellEntityIds.add(link.sourceId);
        if (link.targetId) dwellEntityIds.add(link.targetId);
      }
    });

    const hasDwellExpansion = dwellEntityIds.size > 0;

    return safeLinks.filter((link) => {
      const directlySelected = selectedNodes.has(link.sourceId) || selectedNodes.has(link.targetId);
      if (!hasDwellExpansion) {
        return directlySelected;
      }
      if (link.type === 'dwell') {
        return (
          directlySelected ||
          dwellEntityIds.has(link.sourceId) ||
          dwellEntityIds.has(link.targetId)
        );
      }
      return directlySelected;
    });
  }, [safeLinks, selectedNodesKey, selectedNodes, trackedEntitiesKey, trackedEntities]);

  // ============================================================================
  // DWELL FLOW DATA - Build nodes and edges from dwell transitions
  // ============================================================================
  const dwellFlowData = useMemo(() => {
    // Only process dwell links for the flow map
    const dwellLinks = filteredSafeLinks.filter(l => l.type === 'dwell');
    
    if (dwellLinks.length === 0) {
      return {
        nodes: {} as Record<string, DwellNode>,
        edges: {} as Record<string, DwellFlowEdge>,
        nodeArray: [] as DwellNode[],
        edgeArray: [] as DwellFlowEdge[],
        maxVisits: 1,
        maxWeight: 1,
        hubIds: [] as string[],
      };
    }

    const nodes: Record<string, DwellNode> = {};
    const edges: Record<string, DwellFlowEdge> = {};
    const entityDwellSequence: Record<string, Array<{ locId: string; timestamp: string }>> = {};

    // Track coordinate sums for averaging (centroid calculation)
    const coordSums: Record<string, { latSum: number; lngSum: number; count: number }> = {};

    // First pass: collect all dwell locations and build per-entity sequences
    dwellLinks.forEach((link) => {
      const lat = toFiniteNumber(link.latitude);
      const lng = toFiniteNumber(link.longitude);
      if (lat === null || lng === null) return;

      // Smart grouping: by place name + coarse coords, or tight coords if unnamed
      const locId = dwellLocationId(lat, lng, link.place_name);
      const entityId = link.sourceId; // Entity that dwelled here

      // Upsert node
      if (!nodes[locId]) {
        nodes[locId] = {
          id: locId,
          lat: lat, // Will be averaged later
          lng: lng,
          label: link.place_name || `Location ${Object.keys(nodes).length + 1}`,
          visitCount: 0,
          entityCount: 0,
          entities: [],
          totalDuration: 0,
          connectionCount: 0,
          tier: 3, // Default to tertiary, will be calculated later
          connectedHubs: [],
          pointCount: 0, // Will be set from coordSums.count
        };
        coordSums[locId] = { latSum: 0, lngSum: 0, count: 0 };
      }

      // Accumulate coordinates for centroid calculation
      coordSums[locId].latSum += lat;
      coordSums[locId].lngSum += lng;
      coordSums[locId].count += 1;

      const node = nodes[locId];
      node.visitCount += 1;
      node.totalDuration += (link.weight || 1);
      
      // Update label if we get a better (non-empty) place name
      if (link.place_name && node.label.startsWith('Location ')) {
        node.label = link.place_name;
      }
      
      if (!node.entities.includes(entityId)) {
        node.entities.push(entityId);
        node.entityCount += 1;
      }

      // Build sequence for this entity
      if (!entityDwellSequence[entityId]) {
        entityDwellSequence[entityId] = [];
      }
      entityDwellSequence[entityId].push({
        locId,
        timestamp: (link as any).timestamp || '',
      });
    });

    // Calculate centroid coordinates for each grouped location
    Object.keys(nodes).forEach(locId => {
      const sums = coordSums[locId];
      if (sums && sums.count > 0) {
        nodes[locId].lat = sums.latSum / sums.count;
        nodes[locId].lng = sums.lngSum / sums.count;
        nodes[locId].pointCount = sums.count; // Track how many points were grouped
      }
    });

    // Second pass: create edges from consecutive dwells per entity
    Object.entries(entityDwellSequence).forEach(([entityId, sequence]) => {
      // Sort by timestamp if available
      const sorted = [...sequence].sort((a, b) => {
        if (!a.timestamp || !b.timestamp) return 0;
        return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
      });

      // Create edges between consecutive dwells
      for (let i = 0; i < sorted.length - 1; i++) {
        const fromLoc = sorted[i].locId;
        const toLoc = sorted[i + 1].locId;
        
        // Skip self-loops (same location)
        if (fromLoc === toLoc) continue;

        // Create consistent edge ID (directional)
        const edgeId = `${fromLoc}|${toLoc}`;

        if (!edges[edgeId]) {
          edges[edgeId] = {
            id: edgeId,
            sourceId: fromLoc,
            targetId: toLoc,
            weight: 0,
            entityIds: [],
          };
        }

        const edge = edges[edgeId];
        edge.weight += 1;
        if (!edge.entityIds.includes(entityId)) {
          edge.entityIds.push(entityId);
        }
      }
    });

    // Third pass: count connections per node
    const edgeArray = Object.values(edges);
    edgeArray.forEach(edge => {
      if (nodes[edge.sourceId]) nodes[edge.sourceId].connectionCount += 1;
      if (nodes[edge.targetId]) nodes[edge.targetId].connectionCount += 1;
    });

    // Fourth pass: calculate tiers based on visits and connections
    const nodeArray = Object.values(nodes);
    const maxVisits = nodeArray.length > 0 ? Math.max(...nodeArray.map(n => n.visitCount), 1) : 1;
    const maxConnections = nodeArray.length > 0 ? Math.max(...nodeArray.map(n => n.connectionCount), 1) : 1;
    const maxWeight = edgeArray.length > 0 ? Math.max(...edgeArray.map(e => e.weight), 1) : 1;

    // Calculate importance score and assign tiers
    // Tier 1 (Hub): Top ~10% by importance or >= 5 visits/connections
    // Tier 2 (Secondary): Next ~30% or >= 2 visits/connections
    // Tier 3 (Tertiary): Everything else
    nodeArray.forEach(node => {
      const visitScore = node.visitCount / maxVisits;
      const connectionScore = node.connectionCount / maxConnections;
      const importance = (visitScore * 0.6) + (connectionScore * 0.4); // Weight visits slightly more
      
      if (importance >= 0.3 || node.visitCount >= 5 || node.connectionCount >= 4) {
        node.tier = 1; // Hub
      } else if (importance >= 0.1 || node.visitCount >= 2 || node.connectionCount >= 2) {
        node.tier = 2; // Secondary
      } else {
        node.tier = 3; // Tertiary
      }
    });

    // Fifth pass: link secondary/tertiary nodes to their connected hubs
    const hubIds = nodeArray.filter(n => n.tier === 1).map(n => n.id);
    edgeArray.forEach(edge => {
      const sourceNode = nodes[edge.sourceId];
      const targetNode = nodes[edge.targetId];
      
      // If source is a hub, add it to target's connectedHubs
      if (sourceNode?.tier === 1 && targetNode && !targetNode.connectedHubs.includes(edge.sourceId)) {
        targetNode.connectedHubs.push(edge.sourceId);
      }
      // If target is a hub, add it to source's connectedHubs
      if (targetNode?.tier === 1 && sourceNode && !sourceNode.connectedHubs.includes(edge.targetId)) {
        sourceNode.connectedHubs.push(edge.targetId);
      }
    });

    return {
      nodes,
      edges,
      nodeArray,
      edgeArray,
      maxVisits,
      maxWeight,
      hubIds,
    };
  }, [filteredSafeLinks]);

  // Visible nodes based on selection state - syncs with graph selections
  const visibleNodeIds = useMemo(() => {
    if (!flowMapEnabled) return new Set<string>();

    // If a flow node is selected (clicked in map) AND no graph selection, focus on it + neighbors
    // Graph selection takes priority over flow node selection
    const hasGraphSelection = selectedNodes && selectedNodes.size > 0;
    
    if (selectedFlowNode && !hasGraphSelection) {
      const visible = new Set<string>();
      visible.add(selectedFlowNode);
      dwellFlowData.edgeArray.forEach(edge => {
        if (edge.sourceId === selectedFlowNode) visible.add(edge.targetId);
        if (edge.targetId === selectedFlowNode) visible.add(edge.sourceId);
      });
      return visible;
    }

    // When there's a graph selection, dwellFlowData is already filtered by filteredSafeLinks
    // to only include locations connected to selected entities. Show all of them.
    // This ensures the map syncs with graph selections without re-filtering.
    return new Set(dwellFlowData.nodeArray.map(n => n.id));
  }, [flowMapEnabled, dwellFlowData, selectedFlowNode, selectedNodes]);

  // Generate GeoJSON for flow edges (bezier curves) - only between visible nodes
  const flowEdgesGeoJSON = useMemo(() => {
    if (!flowMapEnabled || dwellFlowData.edgeArray.length === 0) {
      return { type: 'FeatureCollection' as const, features: [] };
    }

    const features: any[] = [];
    
    // Pre-compute which flow nodes are selected via graph (coordinate matching)
    const graphSelectedFlowNodeIds = new Set<string>();
    if (selectedNodes && selectedNodes.size > 0) {
      dwellFlowData.nodeArray.forEach(node => {
        if (isFlowNodeSelectedByGraph(node.lat, node.lng, selectedNodes)) {
          graphSelectedFlowNodeIds.add(node.id);
        }
      });
    }
    const hasGraphLocationSelection = graphSelectedFlowNodeIds.size > 0;

    dwellFlowData.edgeArray.forEach((edge) => {
      // When a node is selected (via map or graph), only keep edges directly touching it
      const edgeTouchesMapSelected = selectedFlowNode && 
        (edge.sourceId === selectedFlowNode || edge.targetId === selectedFlowNode);
      const edgeTouchesGraphSelected = hasGraphLocationSelection &&
        (graphSelectedFlowNodeIds.has(edge.sourceId) || graphSelectedFlowNodeIds.has(edge.targetId));
      
      if ((selectedFlowNode || hasGraphLocationSelection) && !edgeTouchesMapSelected && !edgeTouchesGraphSelected) {
        return;
      }

      const sourceNode = dwellFlowData.nodes[edge.sourceId];
      const targetNode = dwellFlowData.nodes[edge.targetId];
      if (!sourceNode || !targetNode) return;

      // Only show edges between visible nodes
      if (!visibleNodeIds.has(edge.sourceId) || !visibleNodeIds.has(edge.targetId)) {
        return;
      }

      // Generate bezier curve with bundling offset
      const bundleOffset = ((hashString(edge.id) % 11) - 5) * 15;
      const curvePoints = generateBezierCurve(
        [sourceNode.lng, sourceNode.lat],
        [targetNode.lng, targetNode.lat],
        bundleOffset
      );

      // Calculate visual properties
      const normalizedWeight = edge.weight / dwellFlowData.maxWeight;
      const isConnectedToSelected = edgeTouchesMapSelected || edgeTouchesGraphSelected;
      
      // Highlight edges connected to selected node
      const hasActiveSelection = selectedFlowNode || hasGraphLocationSelection;
      const opacity = hasActiveSelection
        ? (isConnectedToSelected ? 0.9 : 0.2)
        : 0.4 + normalizedWeight * 0.4;
      const width = Math.max(1, Math.min(4, 1 + normalizedWeight * 3));

      features.push({
        type: 'Feature',
        properties: {
          id: edge.id,
          weight: edge.weight,
          entityCount: edge.entityIds.length,
          opacity,
          width,
          isHighlighted: isConnectedToSelected,
        },
        geometry: {
          type: 'LineString',
          coordinates: curvePoints,
        },
      });
    });

    return { type: 'FeatureCollection' as const, features };
  }, [flowMapEnabled, dwellFlowData, selectedFlowNode, visibleNodeIds, selectedNodes]);

  // Generate GeoJSON for flow nodes (dwell locations) - hierarchical visibility
  const flowNodesGeoJSON = useMemo(() => {
    if (!flowMapEnabled || dwellFlowData.nodeArray.length === 0) {
      return { type: 'FeatureCollection' as const, features: [] };
    }

    const features: any[] = [];

    // Find connected nodes when selection is active
    const connectedNodeIds = new Set<string>();
    if (selectedFlowNode) {
      dwellFlowData.edgeArray.forEach(edge => {
        if (edge.sourceId === selectedFlowNode) connectedNodeIds.add(edge.targetId);
        if (edge.targetId === selectedFlowNode) connectedNodeIds.add(edge.sourceId);
      });
    }

    dwellFlowData.nodeArray.forEach((node) => {
      // Skip nodes that aren't visible based on hierarchy
      if (!visibleNodeIds.has(node.id)) return;
      
      // Check if selected via map click OR via graph selection (coordinate matching)
      const isSelectedViaMap = selectedFlowNode === node.id;
      const isSelectedViaGraph = isFlowNodeSelectedByGraph(node.lat, node.lng, selectedNodes);
      const isSelected = isSelectedViaMap || isSelectedViaGraph;
      
      const isConnected = selectedFlowNode ? connectedNodeIds.has(node.id) : false;
      const isHub = node.tier === 1;
      const isExpanded = expandedHubs.has(node.id);
      const normalizedVisits = node.visitCount / dwellFlowData.maxVisits;

      // Check if this location is connected to ANY selected entity
      let connectedToSelectedCount = 0;
      if (selectedNodes && selectedNodes.size > 0 && node.entities) {
        connectedToSelectedCount = node.entities.filter(e => selectedNodes.has(e)).length;
      }
      const isConnectedToSelected = connectedToSelectedCount >= 1; // At least 1 selected entity visited
      const isShared = connectedToSelectedCount >= 2; // 2+ selected entities visited (shared)

      // Size based on tier and visit count - larger range for better differentiation
      const tierBaseSize = node.tier === 1 ? 8 : node.tier === 3 ? 6 : 7;
      const sizeBoost = Math.sqrt(normalizedVisits) * 8;
      const selectionBoost = isSelected ? 4 : isConnected ? 3 : 0;
      const sharedBoost = isShared ? 3 : 0; // Shared locations are slightly larger
      const size = Math.max(6, Math.min(32, tierBaseSize + sizeBoost + selectionBoost + sharedBoost));

      // Opacity based on selection state and tier
      let opacity: number;
      if (selectedFlowNode || isSelected) {
        opacity = isSelected ? 1 : isConnected ? 0.95 : 0.3;
      } else {
        opacity = isShared ? 1 : (node.tier === 1 ? 0.95 : node.tier === 2 ? 0.85 : 0.7);
      }

      features.push({
        type: 'Feature',
        properties: {
          id: node.id,
          label: node.label,
          visitCount: node.visitCount,
          entityCount: node.entityCount,
          connectionCount: node.connectionCount,
          tier: node.tier,
          isHub,
          isExpanded,
          isSelected,
          isConnected,
          isConnectedToSelected, // Location visited by 1+ selected entities
          isShared, // Location visited by 2+ selected entities (shared)
          connectedToSelectedCount, // How many selected entities visited this location
          size,
          opacity,
          pointCount: node.pointCount || 1,
          pointCountLabel: (node.pointCount || 1) > 1 ? String(node.pointCount) : '',
        },
        geometry: {
          type: 'Point',
          coordinates: [node.lng, node.lat],
        },
      });
    });

    return { type: 'FeatureCollection' as const, features };
  }, [flowMapEnabled, dwellFlowData, selectedFlowNode, visibleNodeIds, expandedHubs, selectedNodes]);

  // const safeNodes = useMemo(() => {
  //   const result: Array<{
  //     id: string;
  //     label: string;
  //     place_name: string;
  //   }> = [];
  //   for (let i = 0; i < nodes.length; i++) {
  //     const node = nodes[i];
  //     result.push({
  //       id: node.id,
  //       label: node.label || node.id,
  //       place_name: node.place_name || '',
  //     });
  //   }
  //   return result;
  // }, [nodesKey]);

  /* // Process links into location nodes and flow edges
  const flowData = useMemo(() => {
    // Safety limit to prevent stack overflow with large datasets
    const MAX_LINKS = 50000; // Process maximum 50k links to prevent stack overflow
    const processedLinks = filteredSafeLinks.slice(0, MAX_LINKS);

    const locationNodes: Record<string, LocationNode> = {};
    const flowEdges: Record<string, FlowEdge> = {};

    // Use a temporary Set for collecting connected entities (converted to array at end)
    const tempConnectedTo: Record<string, Set<string>> = {};
    // Track link-type counts per location so we can color nodes by dominant type
    const tempTypeCounts: Record<string, Record<string, number>> = {};
    // Track edge types for legend/styling (converted to array at end)
    const tempEdgeTypes: Record<string, Set<string>> = {};

    // Detect whether the dataset contains true "interaction" endpoint locations (e.g., SMS/call with counterparty coords)
    const interactionMode = processedLinks.some((l) => {
      const hasSource = toFiniteNumber((l as any).source_latitude) !== null && toFiniteNumber((l as any).source_longitude) !== null;
      const hasTarget = toFiniteNumber((l as any).target_latitude) !== null && toFiniteNumber((l as any).target_longitude) !== null;
      return hasSource && hasTarget;
    });

    try {
      // First pass: collect all locations from safe links (no circular refs)
      processedLinks.forEach((link) => {
        const sourceId = link.sourceId;
        const targetId = link.targetId;

        // Helper: upsert a location node by coordinate
        const upsertLocation = (lat: number, lng: number, labelHint?: string, entityIds?: string[]) => {
          const locId = locationId(lat, lng);
          if (!locationNodes[locId]) {
            const label =
              (labelHint && String(labelHint).trim().length > 0)
                ? String(labelHint)
                : `Location (${roundCoordinate(lat, 3)}, ${roundCoordinate(lng, 3)})`;
            locationNodes[locId] = {
              id: locId,
              lat: roundCoordinate(lat, 4),
              lng: roundCoordinate(lng, 4),
              label,
              type: link.type || 'unknown',
              connectionCount: 0,
              totalWeight: 0,
              connectedTo: [],
            };
            tempConnectedTo[locId] = new Set<string>();
            tempTypeCounts[locId] = {};
          }

          const node = locationNodes[locId];
          node.connectionCount += 1;
          node.totalWeight += link.weight || 1;

          // Track entities associated with this location (for graph sync on click)
          (entityIds || []).forEach((id) => {
            if (id) tempConnectedTo[locId].add(id);
          });

          const t = link.type || 'unknown';
          const typeCounts = tempTypeCounts[locId] || (tempTypeCounts[locId] = {});
          typeCounts[t] = (typeCounts[t] ?? 0) + 1;

          return locId;
        };

        if (interactionMode) {
          const srcLat = toFiniteNumber((link as any).source_latitude ?? link.latitude);
          const srcLng = toFiniteNumber((link as any).source_longitude ?? link.longitude);
          const dstLat = toFiniteNumber((link as any).target_latitude);
          const dstLng = toFiniteNumber((link as any).target_longitude);

          // We need at least the source location to render the sender/owner-side dot
          if (srcLat === null || srcLng === null) return;

          const srcLocId = upsertLocation(
            srcLat,
            srcLng,
            (link as any).source_place_name ?? link.place_name,
            [sourceId]
          );

          // If counterparty coords exist, add destination location and create a direct interaction edge
          if (dstLat !== null && dstLng !== null) {
            const dstLocId = upsertLocation(dstLat, dstLng, undefined, [targetId]);

            const edgeId = `${srcLocId}|${dstLocId}|${link.type || 'unknown'}`;
            if (!flowEdges[edgeId]) {
              flowEdges[edgeId] = {
                id: edgeId,
                sourceId: srcLocId,
                targetId: dstLocId,
                weight: 0,
                types: [],
                count: 0,
              };
            }
            const edge = flowEdges[edgeId];
            edge.weight += link.weight || 1;
            edge.count += 1;
            // Store types for styling/legend (deduped later)
            tempEdgeTypes[edgeId] = tempEdgeTypes[edgeId] || new Set<string>();
            tempEdgeTypes[edgeId].add(link.type || 'unknown');
          }

          return;
        }

        // Legacy mode: single coordinate per link (dwells/browsing/app/etc.)
        const lat = toFiniteNumber(link.latitude);
        const lng = toFiniteNumber(link.longitude);
        if (lat === null || lng === null) return;

        upsertLocation(lat, lng, link.place_name, [sourceId, targetId]);
      });
    } catch (error) {
      console.warn('Error processing location nodes:', error);
      return {
        locationNodes: {},
        flowEdges: {},
        locationArray: [],
        flowEdgeArray: [],
        maxConnections: 1,
        maxEdgeWeight: 1,
      };
    }
    
    // Convert Sets to arrays
    Object.keys(locationNodes).forEach(locId => {
      locationNodes[locId].connectedTo = Array.from(tempConnectedTo[locId] || []);

      // Pick dominant type for stable coloring (matches legend mapping)
      const typeCounts = tempTypeCounts[locId];
      if (typeCounts) {
        let bestType = locationNodes[locId].type || 'unknown';
        let bestCount = -1;
        for (const [t, c] of Object.entries(typeCounts)) {
          if (c > bestCount) {
            bestCount = c;
            bestType = t;
          }
        }
        locationNodes[locId].type = bestType;
      }
    });
    
    // Second pass: create edges between locations that share entities (legacy mode only)
    const locationArray = Object.values(locationNodes);

    if (interactionMode) {
      // Finalize edge types for interaction edges
      try {
        Object.keys(flowEdges).forEach(edgeId => {
          flowEdges[edgeId].types = Array.from(tempEdgeTypes[edgeId] || []);
        });
        const flowEdgeArray = Object.values(flowEdges);
        const maxConnections = locationArray.length > 0
          ? Math.max(...locationArray.map(n => n.connectionCount), 1)
          : 1;
        const maxEdgeWeight = flowEdgeArray.length > 0
          ? Math.max(...flowEdgeArray.map(e => e.weight), 1)
          : 1;
        return {
          locationNodes,
          flowEdges,
          locationArray,
          flowEdgeArray,
          maxConnections,
          maxEdgeWeight,
        };
      } catch (error) {
        console.warn('Error finalizing interaction flow data:', error);
        return {
          locationNodes,
          flowEdges,
          locationArray: [],
          flowEdgeArray: [],
          maxConnections: 1,
          maxEdgeWeight: 1,
        };
      }
    }

    // Limit edge creation to prevent stack overflow with large datasets
    const MAX_EDGES = 10000; // Maximum number of edges to create
    let edgeCount = 0;

    try {
      // Group locations by the entities they're connected to
      const entityToLocations: Record<string, string[]> = {};
      locationArray.forEach(loc => {
        loc.connectedTo.forEach(entityId => {
          if (!entityToLocations[entityId]) {
            entityToLocations[entityId] = [];
          }
          if (!entityToLocations[entityId].includes(loc.id)) {
            entityToLocations[entityId].push(loc.id);
          }
        });
      });

      // Create edges between locations that share entities
      // Limit the number of entities processed to prevent excessive computation
      const entityKeys = Object.keys(entityToLocations).slice(0, 1000); // Max 1000 entities

      entityKeys.forEach(entityId => {
        const locArray = entityToLocations[entityId];
        if (!locArray || locArray.length < 2) return;

        // Limit pairs per entity to prevent O(n²) explosion
        const maxPairs = Math.min(100, locArray.length * (locArray.length - 1) / 2); // Max 100 pairs per entity
        let pairsCreated = 0;

        for (let i = 0; i < locArray.length && pairsCreated < maxPairs; i++) {
          for (let j = i + 1; j < locArray.length && pairsCreated < maxPairs; j++) {
            if (edgeCount >= MAX_EDGES) break;

            const loc1 = locationNodes[locArray[i]];
            const loc2 = locationNodes[locArray[j]];
            if (!loc1 || !loc2) continue;

            // Create edge ID (sorted to avoid duplicates)
            const edgeId = [locArray[i], locArray[j]].sort().join('|');

            if (!flowEdges[edgeId]) {
              flowEdges[edgeId] = {
                id: edgeId,
                sourceId: loc1.id,
                targetId: loc2.id,
                weight: 0,
                types: [], // Will be populated at end
                count: 0,
              };
              tempEdgeTypes[edgeId] = new Set<string>();
            }

            const edge = flowEdges[edgeId];
            edge.weight += 1;
            edge.count += 1;
            tempEdgeTypes[edgeId].add(loc1.type);
            tempEdgeTypes[edgeId].add(loc2.type);

            pairsCreated++;
            edgeCount++;
          }
          if (edgeCount >= MAX_EDGES) break;
        }
        if (edgeCount >= MAX_EDGES) return; // Exit early if we've hit the limit
      });
    } catch (error) {
      console.warn('Error creating flow edges:', error);
      // Continue with empty edges if there's an error
    }
    
    try {
      // Convert edge type Sets to arrays
      Object.keys(flowEdges).forEach(edgeId => {
        flowEdges[edgeId].types = Array.from(tempEdgeTypes[edgeId] || []);
      });

      // Calculate max values for normalization (safely handle large arrays)
      const maxConnections = locationArray.length > 0
        ? Math.max(...locationArray.map(n => n.connectionCount), 1)
        : 1;
      const flowEdgeArray = Object.values(flowEdges);
      const maxEdgeWeight = flowEdgeArray.length > 0
        ? Math.max(...flowEdgeArray.map(e => e.weight), 1)
        : 1;

      return {
        locationNodes,
        flowEdges,
        locationArray,
        flowEdgeArray,
        maxConnections,
        maxEdgeWeight,
      };
    } catch (error) {
      console.warn('Error finalizing flow data:', error);
      return {
        locationNodes,
        flowEdges,
        locationArray: [],
        flowEdgeArray: [],
        maxConnections: 1,
        maxEdgeWeight: 1,
      };
    }
  }, [filteredSafeLinks, safeNodes]); */

  /**
   * Compute the visible flow nodes ONCE and share the result between
   * the node layer and the edge layer. This prevents edges from rendering
   * to endpoints that are not currently rendered as nodes ("lines to nowhere").
   */
  /* const visibleFlowNodeIds = useMemo(() => {
    if (!flowMapEnabled) return new Set<string>();

    const MAX_RENDERED_NODES = 2000;
    const highZoom = currentZoom >= 13;
    const entitySelectionActive = selectedNodes && selectedNodes.size > 0;
    
    // Debug: log entity selection
    if (entitySelectionActive) {
      console.log('[FlowMap] Entity selection from graph:', Array.from(selectedNodes));
      console.log('[FlowMap] Sample location connectedTo:', flowData.locationArray.slice(0, 3).map(l => ({ id: l.id, label: l.label, connectedTo: l.connectedTo })));
    }

    // If a node is selected, pull its neighbors into the visible set (even if they'd be dropped by the cap)
    // so the UI can actually highlight them.
    const neighborWeights = new globalThis.Map<string, number>();
    if (selectedFlowNode) {
      for (let i = 0; i < flowData.flowEdgeArray.length; i++) {
        const e = flowData.flowEdgeArray[i];
        if (e.sourceId === selectedFlowNode) {
          neighborWeights.set(e.targetId, (neighborWeights.get(e.targetId) ?? 0) + (e.weight ?? 1));
        } else if (e.targetId === selectedFlowNode) {
          neighborWeights.set(e.sourceId, (neighborWeights.get(e.sourceId) ?? 0) + (e.weight ?? 1));
        }
      }
    }

    const candidates = flowData.locationArray
      .filter((n) => {
        const isSelected = selectedFlowNode === n.id;
        const isNeighbor = selectedFlowNode ? neighborWeights.has(n.id) : false;
        if (selectedFlowNode && focusSelection) {
          // In focus mode, only the selected node and its neighbors remain visible
          return isSelected || isNeighbor;
        }
        if (!selectedFlowNode && entitySelectionActive) {
          // When selection comes from the main graph (entity ids), only show locations connected to those entities
          return n.connectedTo?.some((id) => selectedNodes.has(id));
        }
        const isHub = n.connectionCount >= hubThreshold;
        return isHub || isSelected || isNeighbor || highZoom;
      })
      .map((n) => ({
        id: n.id,
        isSelected: selectedFlowNode === n.id,
        isHub: n.connectionCount >= hubThreshold,
        isNeighbor: selectedFlowNode ? neighborWeights.has(n.id) : false,
        neighborWeight: selectedFlowNode ? (neighborWeights.get(n.id) ?? 0) : 0,
        connectionCount: n.connectionCount,
      }));

    // Prioritize: selected > neighbors (by edge weight) > hubs > highest degree
    candidates.sort((a, b) => {
      if (a.isSelected !== b.isSelected) return a.isSelected ? -1 : 1;
      if (a.isNeighbor !== b.isNeighbor) return a.isNeighbor ? -1 : 1;
      if (a.neighborWeight !== b.neighborWeight) return b.neighborWeight - a.neighborWeight;
      if (a.isHub !== b.isHub) return a.isHub ? -1 : 1;
      return b.connectionCount - a.connectionCount;
    });

    return new Set<string>(candidates.slice(0, MAX_RENDERED_NODES).map((c) => c.id));
  }, [flowMapEnabled, flowData.locationArray, flowData.flowEdgeArray, hubThreshold, selectedFlowNode, currentZoom, focusSelection, selectedNodesKey, selectedNodes]); */


  // Generate flow edge GeoJSON with bezier curves
  // NOTE: Flow map feature currently disabled - keeping code for future use
  /* const flowEdgesGeoJSON = useMemo(() => {
    if (!flowMapEnabled) return { type: 'FeatureCollection', features: [] };

    const features: any[] = [];
    const MAX_RENDERED_EDGES = 5000; // Limit rendered edges to prevent performance issues

    // DEBUG: Log selection state
    console.log('[FlowMap Edges] selectedFlowNode:', selectedFlowNode);
    console.log('[FlowMap Edges] Total edges in flowData:', flowData.flowEdgeArray.length);

    try {
      const eligibleEdges = flowData.flowEdgeArray
        .filter((edge) => visibleFlowNodeIds.has(edge.sourceId) && visibleFlowNodeIds.has(edge.targetId))
        .slice(0, MAX_RENDERED_EDGES);

      console.log('[FlowMap Edges] Eligible edges (both endpoints visible):', eligibleEdges.length);

      let skippedNotConnected = 0;
      let skippedDistance = 0;
      let rendered = 0;

      eligibleEdges.forEach((edge) => {
      // Look up source and target nodes
      const sourceNode = flowData.locationNodes[edge.sourceId];
      const targetNode = flowData.locationNodes[edge.targetId];
      if (!sourceNode || !targetNode) return;

      // Calculate distance between endpoints
      const distanceKm = calculateDistanceKm(
        sourceNode.lat, sourceNode.lng,
        targetNode.lat, targetNode.lng
      );

      // Skip edges that exceed the max distance (unless it's a selected connection)
      const isSourceSelected = selectedFlowNode === edge.sourceId;
      const isTargetSelected = selectedFlowNode === edge.targetId;
      const isConnectedToSelected = isSourceSelected || isTargetSelected;

      // When a node is selected, ONLY show edges directly connected to it
      // Don't show edges between neighbors that don't involve the selected node
      if (selectedFlowNode && !isConnectedToSelected) {
        skippedNotConnected++;
        return;
      }

      if (distanceKm > maxDistanceKm && !isConnectedToSelected) {
        skippedDistance++;
        return;
      }
      
      rendered++;

      // Note: We already filtered edges to those where BOTH endpoints are in visibleFlowNodeIds,
      // so we don't need a secondary visibility check here. The visibleFlowNodeIds logic already
      // accounts for hubs, selected nodes, neighbors, and zoom level correctly.
      
      // Calculate bezier curve
      // Use deterministic bounded offsets (instead of global edge index) to avoid extreme arcs at scale
      const bundleOffset = bundleLinks ? ((hashString(edge.id) % 11) - 5) * 15 : 0;
      const curvePoints = generateBezierCurve(
        [sourceNode.lng, sourceNode.lat],
        [targetNode.lng, targetNode.lat],
        bundleOffset
      );
      
      // Calculate opacity based on weight
      const normalizedWeight = edge.weight / flowData.maxEdgeWeight;
      const baseOpacity = adaptiveOpacity ? 0.2 + normalizedWeight * 0.6 : 0.5;
      const opacity = selectedFlowNode
        ? (isConnectedToSelected ? 0.95 : Math.min(baseOpacity, 0.15))
        : baseOpacity;
      
      // Determine color based on dominant type
      let color = '#6366f1'; // Default indigo
      if (edge.types.length === 1) {
        const typeColorMap: Record<string, string> = {
          'dwell': '#ef4444',
          'call': '#3b82f6',
          'sms': '#10b981',
          'browsing': '#f59e0b',
          'app': '#7c3aed',
        };
        color = typeColorMap[edge.types[0]] || color;
      }
      
      features.push({
        type: 'Feature',
        properties: {
          id: edge.id,
          weight: edge.weight,
          opacity,
          color,
          // Thinner flow lines (px): keep subtle weight variation without dominating the map
          width: Math.max(0.75, Math.min(2.5, 0.75 + normalizedWeight * 1.75)),
          isHighlighted: isConnectedToSelected,
        },
        geometry: {
          type: 'LineString',
          coordinates: curvePoints,
        },
      });
      });

      console.log('[FlowMap Edges] Summary:', {
        selectedFlowNode,
        skippedNotConnected,
        skippedDistance,
        rendered,
        totalFeatures: features.length,
      });

      // If a node is selected, log what edges were rendered
      if (selectedFlowNode && features.length > 0) {
        console.log('[FlowMap Edges] Rendered edges for selected node:', 
          features.slice(0, 5).map((f: any) => f.properties.id)
        );
      }

      return {
        type: 'FeatureCollection',
        features,
      };
    } catch (error) {
      console.warn('Error generating flow edges GeoJSON:', error);
      return { type: 'FeatureCollection', features: [] };
    }
  }, [flowMapEnabled, flowData, selectedFlowNode, adaptiveOpacity, bundleLinks, maxDistanceKm, visibleFlowNodeIds, focusSelection]); */

  // Generate flow node GeoJSON
  // NOTE: Flow map feature currently disabled - keeping code for future use
  /* const flowNodesGeoJSON = useMemo(() => {
    if (!flowMapEnabled) return { type: 'FeatureCollection', features: [] };

    const features: any[] = [];

    try {
      // Precompute neighbors of the selected node for styling
      const connectedIds = new Set<string>();
      if (selectedFlowNode) {
        for (let i = 0; i < flowData.flowEdgeArray.length; i++) {
          const e = flowData.flowEdgeArray[i];
          if (e.sourceId === selectedFlowNode) connectedIds.add(e.targetId);
          else if (e.targetId === selectedFlowNode) connectedIds.add(e.sourceId);
        }
      }

      // Render exactly the same set of nodes that edges are allowed to target
      Array.from(visibleFlowNodeIds).forEach((nodeId) => {
      const node = flowData.locationNodes[nodeId];
      if (!node) return;

      const isHub = node.connectionCount >= hubThreshold;
      const isSelected = selectedFlowNode === node.id;
      const isConnected = selectedFlowNode ? connectedIds.has(node.id) : false;
      
      // Calculate size based on connection count
      const normalizedConnections = node.connectionCount / flowData.maxConnections;
      // Use a sqrt curve + clamp so low-degree nodes don't look huge, while hubs still stand out.
      const clamped = Math.max(0, Math.min(1, normalizedConnections));
      const baseSize = isHub ? 2.5 : 2;
      const scaled = Math.sqrt(clamped) * 7.5;
      const emphasis = isSelected ? 1.15 : isConnected ? 1.05 : 1;
      const size = Math.max(2.5, Math.min(14, (baseSize + scaled) * emphasis));
      
      // Calculate opacity
      const baseOpacity = adaptiveOpacity
        ? (isHub ? 0.9 : 0.3 + normalizedConnections * 0.5)
        : 0.8;
      const opacity = selectedFlowNode
        ? (isSelected ? 1 : isConnected ? Math.max(baseOpacity, 0.9) : (focusSelection ? 0 : Math.min(baseOpacity, 0.15)))
        : baseOpacity;
      
      // Determine color based on type
      const typeColorMap: Record<string, string> = {
        'dwell': '#ef4444',
        'call': '#3b82f6',
        'sms': '#10b981',
        'browsing': '#f59e0b',
        'app': '#7c3aed',
      };
      const color = typeColorMap[node.type] || '#6366f1';
      
      features.push({
        type: 'Feature',
        properties: {
          id: node.id,
          label: node.label,
          type: node.type,
          connectionCount: node.connectionCount,
          isHub,
          isSelected,
          isConnected,
          hasActiveSelection: !!selectedFlowNode,
          size,
          opacity: isSelected ? 1 : opacity,
          color,
        },
        geometry: {
          type: 'Point',
          coordinates: [node.lng, node.lat],
        },
        });
      });

  return {
        type: 'FeatureCollection',
        features,
      };
    } catch (error) {
      console.warn('Error generating flow nodes GeoJSON:', error);
      return { type: 'FeatureCollection', features: [] };
    }
  }, [flowMapEnabled, flowData, selectedFlowNode, currentZoom, adaptiveOpacity, hubThreshold, visibleFlowNodeIds, focusSelection]); */

  // ============================================================================
  // STANDARD VIEW DATA (existing functionality)
  // ============================================================================

  // Extract location markers from links (standard clustered view)
  // Use safeLinks to avoid circular reference issues from force-directed graph
  const geojsonData = useMemo(() => {
    const features: any[] = [];

    filteredSafeLinks.forEach((link, index) => {
      const coords = toValidLngLat(link.longitude, link.latitude);
      if (coords) {
        features.push({
          type: 'Feature',
          properties: {
            id: `${link.type || 'connection'}-${index}`,
            type: link.type || 'unknown',
            device_id: link.sourceId || '',
            target: link.targetId,
            location_name: link.place_name || '',
          },
          geometry: {
            type: 'Point',
            coordinates: coords,
          },
        });
      }
    });

    return {
      type: 'FeatureCollection',
      features,
    };
  }, [filteredSafeLinks]);

  // Determine cluster color based on whether all features are the same type
  const clusterColor = useMemo(() => {
    if (geojsonData.features.length === 0) return '#9ca3af';
    
    const types = new Set<string>(
      geojsonData.features.map((f: any) => String(f.properties?.type ?? ''))
    );
    
    if (types.size === 1) {
      const singleType = Array.from(types)[0] as string;
      const colorMap: Record<string, string> = {
        'dwell': '#ef4444',
        'call': '#3b82f6',
        'sms': '#10b981',
        'browsing': '#f59e0b',
        'app': '#7c3aed',
      };
      return colorMap[singleType] || '#9ca3af';
    }
    
    return '#9ca3af';
  }, [geojsonData]);

  // Calculate map bounds to fit all markers
  const bounds = useMemo(() => {
    if (geojsonData.features.length === 0) return null;

    const coords = geojsonData.features.map((f: any) => f.geometry.coordinates);
    const lngs = coords.map((c: number[]) => c[0]);
    const lats = coords.map((c: number[]) => c[1]);

    const minLng = Math.min(...lngs);
    const minLat = Math.min(...lats);
    const maxLng = Math.max(...lngs);
    const maxLat = Math.max(...lats);
    if (![minLng, minLat, maxLng, maxLat].every(Number.isFinite)) return null;

    return {
      minLng,
      minLat,
      maxLng,
      maxLat,
    };
  }, [geojsonData]);

  // Fit map to bounds when markers change
  useEffect(() => {
    if (mapRef.current && bounds && geojsonData.features.length > 1) {
      mapRef.current.fitBounds(
        [
          [bounds.minLng, bounds.minLat],
          [bounds.maxLng, bounds.maxLat],
        ],
        { padding: 40, duration: 1000 }
      );
    }
  }, [bounds, geojsonData.features.length]);

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  // Handle cluster click - zoom into cluster
  const onClusterClick = (event: MapLayerMouseEvent) => {
    if (isDrawing) return;
    
    const feature = event.features?.[0];
    if (!feature || !mapRef.current) return;

    const clusterId = feature.properties?.cluster_id;
    const source = mapRef.current.getSource('dwells') as any;

    if (source && source.getClusterExpansionZoom) {
      source.getClusterExpansionZoom(clusterId, (err: any, zoom: number) => {
        if (err || !feature.geometry || feature.geometry.type !== 'Point') return;
        const coords = (feature.geometry.coordinates ?? []) as unknown[];
        const center = toValidLngLat(coords[0], coords[1]);
        if (!center) return;
        if (!Number.isFinite(zoom)) return;

        mapRef.current?.easeTo({
          center,
          zoom: zoom,
          duration: 500,
        });
      });
    }
  };

  // Handle flow node click - expand hubs, select nodes, filter graph
  // Ctrl+click adds to selection instead of replacing
  const onFlowNodeClick = useCallback((event: MapLayerMouseEvent) => {
    if (isDrawing || !flowMapEnabled) return;
    
    const ctrlKey = event.originalEvent?.ctrlKey || event.originalEvent?.metaKey || false;
    
    const feature = event.features?.[0];
    if (!feature) {
      // Clicked on map background - clear selection and collapse all hubs
      setSelectedFlowNode(null);
      setFlowPopupInfo(null);
      setExpandedHubs(new Set());
      // Clear graph filter (only if not Ctrl+clicking to preserve selection)
      if (onNodeClick && !ctrlKey) {
        onNodeClick('', []);
      }
      return;
    }

    // It's a node click
    const nodeId = feature.properties?.id;
    if (!nodeId) return;

    const node = dwellFlowData.nodes[nodeId];
    if (!node) return;

    const isHub = node.tier === 1;
    const isCurrentlyExpanded = expandedHubs.has(nodeId);
    const isCurrentlySelected = selectedFlowNode === nodeId;

    // Hub click behavior: toggle expansion
    if (isHub) {
      if (isCurrentlyExpanded) {
        // Collapse this hub
        const newExpanded = new Set(expandedHubs);
        newExpanded.delete(nodeId);
        setExpandedHubs(newExpanded);
        
        // If this was also selected, clear selection
        if (isCurrentlySelected) {
          setSelectedFlowNode(null);
          setFlowPopupInfo(null);
          if (onNodeClick && !ctrlKey) onNodeClick('', []);
        }
      } else {
        // Expand this hub and select it
        const newExpanded = new Set(expandedHubs);
        newExpanded.add(nodeId);
        setExpandedHubs(newExpanded);
        
        setSelectedFlowNode(nodeId);
        setFlowPopupInfo({ node, lng: node.lng, lat: node.lat });
        
        // Filter graph to show entities at this hub
        if (onNodeClick) {
          // Focus graph on the selected location, its direct neighbor locations, and the entities at this location
          const neighborLocationIds = new Set<string>();
          dwellFlowData.edgeArray.forEach(edge => {
            if (edge.sourceId === nodeId) neighborLocationIds.add(edge.targetId);
            if (edge.targetId === nodeId) neighborLocationIds.add(edge.sourceId);
          });
          const entityIds = node.entities ?? [];
          const allNodeIds = [nodeId, ...Array.from(neighborLocationIds), ...entityIds];
          onNodeClick(node.label || nodeId, allNodeIds, ctrlKey);
        }
      }
    } else {
      // Non-hub node: toggle selection
      if (isCurrentlySelected && !ctrlKey) {
        setSelectedFlowNode(null);
        setFlowPopupInfo(null);
        if (onNodeClick) onNodeClick('', []);
      } else {
        setSelectedFlowNode(nodeId);
        setFlowPopupInfo({ node, lng: node.lng, lat: node.lat });
        
        // Filter graph
        if (onNodeClick) {
          // Focus graph on the selected location, its direct neighbor locations, and the entities at this location
          const neighborLocationIds = new Set<string>();
          dwellFlowData.edgeArray.forEach(edge => {
            if (edge.sourceId === nodeId) neighborLocationIds.add(edge.targetId);
            if (edge.targetId === nodeId) neighborLocationIds.add(edge.sourceId);
          });
          const entityIds = node.entities ?? [];
          const allNodeIds = [nodeId, ...Array.from(neighborLocationIds), ...entityIds];
          onNodeClick(node.label || nodeId, allNodeIds, ctrlKey);
        }
      }
    }
  }, [isDrawing, flowMapEnabled, dwellFlowData.nodes, selectedFlowNode, expandedHubs, onNodeClick, filteredSafeLinks]);

  // Track zoom level changes
  const handleZoomEnd = useCallback(() => {
    if (mapRef.current) {
      setCurrentZoom(mapRef.current.getZoom());
    }
  }, []);

  // Toggle area selection mode
  const toggleDrawMode = useCallback(() => {
    setIsDrawing(!isDrawing);
    setDrawStart(null);
    setDrawEnd(null);
  }, [isDrawing]);

  // Clear area selection
  const clearAreaSelection = useCallback(() => {
    setDrawStart(null);
    setDrawEnd(null);
    setIsDrawing(false);
    if (onAreaSelect) {
      onAreaSelect(null);
    }
  }, [onAreaSelect]);

  // Handle map mouse events for drawing
  const handleMapMouseDown = useCallback((event: MapLayerMouseEvent) => {
    if (!isDrawing) return;
    event.preventDefault();
    setDrawStart({ lng: event.lngLat.lng, lat: event.lngLat.lat });
    setDrawEnd(null);
  }, [isDrawing]);

  const handleMapMouseMove = useCallback((event: MapLayerMouseEvent) => {
    if (!isDrawing || !drawStart) return;
    setDrawEnd({ lng: event.lngLat.lng, lat: event.lngLat.lat });
  }, [isDrawing, drawStart]);

  const handleMapMouseUp = useCallback((event: MapLayerMouseEvent) => {
    if (!isDrawing || !drawStart) return;
    
    const endPoint = { lng: event.lngLat.lng, lat: event.lngLat.lat };
    setDrawEnd(endPoint);
    
    const bounds: AreaBounds = {
      minLng: Math.min(drawStart.lng, endPoint.lng),
      maxLng: Math.max(drawStart.lng, endPoint.lng),
      minLat: Math.min(drawStart.lat, endPoint.lat),
      maxLat: Math.max(drawStart.lat, endPoint.lat),
    };
    
    const lngDiff = Math.abs(drawStart.lng - endPoint.lng);
    const latDiff = Math.abs(drawStart.lat - endPoint.lat);
    if (lngDiff > 0.0001 && latDiff > 0.0001) {
      if (onAreaSelect) {
        onAreaSelect(bounds);
      }
      setIsDrawing(false);
    }
  }, [isDrawing, drawStart, onAreaSelect]);

  // Generate GeoJSON for the selection rectangle
  const selectionRectangle = useMemo(() => {
    let bounds: AreaBounds | null = null;
    
    if (drawStart && drawEnd) {
      bounds = {
        minLng: Math.min(drawStart.lng, drawEnd.lng),
        maxLng: Math.max(drawStart.lng, drawEnd.lng),
        minLat: Math.min(drawStart.lat, drawEnd.lat),
        maxLat: Math.max(drawStart.lat, drawEnd.lat),
      };
    } else if (selectedArea) {
      bounds = selectedArea;
    }
    
    if (!bounds) return null;
    
    return {
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'Polygon',
        coordinates: [[
          [bounds.minLng, bounds.minLat],
          [bounds.maxLng, bounds.minLat],
          [bounds.maxLng, bounds.maxLat],
          [bounds.minLng, bounds.maxLat],
          [bounds.minLng, bounds.minLat],
        ]],
      },
    };
  }, [drawStart, drawEnd, selectedArea]);

  // ============================================================================
  // RENDER
  // ============================================================================

  const hasData = geojsonData.features.length > 0 || flowNodesGeoJSON.features.length > 0;
  const interactiveLayerIds = isDrawing 
    ? [] 
    : flowMapEnabled 
      ? ['flow-nodes']
      : ['clusters', 'unclustered-point'];

  // Check if we have dwell flow data to show the toggle
  const hasFlowData = dwellFlowData.nodeArray.length > 0;

  return (
    <Box ref={mapContainerRef} id="network-map-container" sx={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Map */}
      <Box sx={{ flex: 1, position: 'relative' }}>
        {!hasData ? (
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              height: '100%',
              flexDirection: 'column',
              gap: 2,
              p: 3,
              textAlign: 'center'
            }}
          >
            <LocationOnIcon sx={{ fontSize: 60, color: 'text.disabled' }} />
            <Typography variant="body2" color="text.secondary">
              No location data available for current selection.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Location data available from dwells, calls, SMS, and browsing connections.
            </Typography>
          </Box>
        ) : (
          <Map
            ref={mapRef}
            initialViewState={{
              longitude: (() => {
                const v = geojsonData.features[0]?.geometry?.coordinates?.[0];
                return toFiniteNumber(v) ?? DEFAULT_CENTER[0];
              })(),
              latitude: (() => {
                const v = geojsonData.features[0]?.geometry?.coordinates?.[1];
                return toFiniteNumber(v) ?? DEFAULT_CENTER[1];
              })(),
              zoom: geojsonData.features.length === 1 ? 14 : DEFAULT_ZOOM,
            }}
            style={{ width: '100%', height: '100%', cursor: isDrawing ? 'crosshair' : 'grab' }}
            mapStyle={getMapboxStyle(mapStyle)}
            mapboxAccessToken={MAPBOX_TOKEN}
            projection={{name: 'mercator'}}
            preserveDrawingBuffer={true}
            interactiveLayerIds={interactiveLayerIds}
            onClick={flowMapEnabled ? onFlowNodeClick : onClusterClick}
            onMouseDown={handleMapMouseDown}
            onMouseMove={handleMapMouseMove}
            onMouseUp={handleMapMouseUp}
            onZoomEnd={handleZoomEnd}
            dragPan={!isDrawing}
            dragRotate={!isDrawing}
          >
            {/* Navigation Controls */}
            <NavigationControl position="top-right" style={{ transform: 'scale(0.8)', transformOrigin: 'top right' }} />
            <ScaleControl position="bottom-right" style={{ transform: 'scale(0.8)', transformOrigin: 'bottom right' }} />

            {/* Map Style Control - positioned under zoom controls */}
            <Box
              sx={{
                position: 'absolute',
                top: 90,
                right: 8,
                zIndex: 1000,
                '& > div': { position: 'static !important', margin: 0 },
                '& button': { width: 32, height: 32, '& svg': { fontSize: '1.2rem' } }
              }}
            >
              <MapStyleControl
                selectedMapStyle={mapStyle}
                onMapStyleChange={setMapStyle}
                position="top-right"
              />
            </Box>
            
            {/* Legend Toggle Button - under map style control */}
            <Tooltip title="Map Legend" placement="left">
              <IconButton
                onClick={() => setInfoPanelOpen(!infoPanelOpen)}
                sx={{
                  position: 'absolute',
                  top: 130,
                  right: 8,
                  zIndex: 1000,
                  width: 32,
                  height: 32,
                  bgcolor: alpha(theme.palette.background.paper, 0.95),
                  backdropFilter: 'blur(8px)',
                  border: '1px solid',
                  borderColor: infoPanelOpen ? 'primary.main' : 'divider',
                  '&:hover': { bgcolor: alpha(theme.palette.background.paper, 1) },
                }}
                size="small"
              >
                <InfoOutlinedIcon fontSize="small" color={infoPanelOpen ? 'primary' : 'inherit'} />
              </IconButton>
            </Tooltip>
            
            {/* Legend Panel - positioned near the button */}
            {infoPanelOpen && (
              <Paper
                elevation={3}
                sx={{
                  position: 'absolute',
                  top: 170,
                  right: 8,
                  zIndex: 1000,
                  p: 1.5,
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  backdropFilter: 'blur(8px)',
                  minWidth: 180,
                  maxWidth: 220,
                }}
              >
                <Typography variant="caption" fontWeight="bold" sx={{ display: 'block', mb: 1 }}>
                  Map Legend
                </Typography>
                
                {/* Location markers */}
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontSize: '0.65rem' }}>
                    Locations
                  </Typography>
                  <Stack spacing={0.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CircleIcon sx={{ fontSize: 12, color: '#f59e0b' }} />
                      <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>Dwell Location</Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CircleIcon sx={{ fontSize: 12, color: '#E91E63' }} />
                      <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>General Location</Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box sx={{ 
                        width: 12, 
                        height: 12, 
                        borderRadius: '50%', 
                        bgcolor: '#fff',
                        border: '2px solid #00BFFF',
                        animation: 'pulse 1.5s infinite',
                      }} />
                      <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>Selected Location</Typography>
                    </Stack>
                  </Stack>
                </Box>
                
                {/* Flow lines (when enabled) */}
                {flowMapEnabled && (
                  <Box sx={{ mb: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontSize: '0.65rem' }}>
                      Traffic Flow
                    </Typography>
                    <Stack spacing={0.5}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box sx={{ width: 20, height: 2, bgcolor: 'rgba(245,158,11,0.6)', borderRadius: 1 }} />
                        <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>Movement Path</Typography>
                      </Stack>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box sx={{ width: 20, height: 4, bgcolor: 'rgba(245,158,11,0.8)', borderRadius: 1 }} />
                        <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>Frequent Route</Typography>
                      </Stack>
                    </Stack>
                  </Box>
                )}
                
                {/* Selection info */}
                {selectedNodes.size > 0 && (
                  <Box sx={{ pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="caption" color="primary.main" sx={{ display: 'block', fontSize: '0.65rem' }}>
                      {selectedNodes.size} item{selectedNodes.size > 1 ? 's' : ''} selected
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                      Map filtered to selection
                    </Typography>
                  </Box>
                )}
              </Paper>
            )}

            {/* Dwell Flow Map Toggle - TEMPORARILY DISABLED for debugging */}
            {/* TODO: Re-enable when flow map sync issues are resolved */}
            {false && hasFlowData && isDwellsOnlySelected && (
              <Paper
                elevation={2}
                sx={{
                  position: 'absolute',
                  top: 8,
                  left: 8,
                  zIndex: 1000,
                  p: 0.75,
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  backdropFilter: 'blur(8px)',
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={flowMapEnabled}
                      onChange={(e) => {
                        setFlowMapEnabled(e.target.checked);
                        setSelectedFlowNode(null);
                        setFlowPopupInfo(null);
                        // Clear graph filter when disabling
                        if (!e.target.checked && onNodeClick) {
                          onNodeClick('', []);
                        }
                      }}
                      size="small"
                      color="primary"
                    />
                  }
                  label={
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <TimelineIcon sx={{ fontSize: 16, color: flowMapEnabled ? 'primary.main' : 'text.secondary' }} />
                      <Typography variant="caption" fontWeight={flowMapEnabled ? 'bold' : 'normal'}>
                        Dwell Traffic Flow
                      </Typography>
                    </Stack>
                  }
                  sx={{ m: 0 }}
                />
              </Paper>
            )}

            {/* Info Panel Toggle Button - shown when legend would be shown */}
            {showLegend && !flowMapEnabled && (
              <>
                {/* Collapsed state - just show toggle button */}
                {!infoPanelOpen && (
                  <IconButton
                    onClick={() => setInfoPanelOpen(true)}
                    sx={{
                      position: 'absolute',
                      bottom: 48,
                      left: 8,
                      zIndex: 1000,
                      bgcolor: alpha(theme.palette.background.paper, 0.95),
                      backdropFilter: 'blur(8px)',
                      '&:hover': { bgcolor: alpha(theme.palette.background.paper, 1) },
                    }}
                    size="small"
                  >
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                )}
                
                {/* Expanded state - show full info panel */}
                {infoPanelOpen && (
                  <Paper
                    elevation={2}
                    sx={{
                      position: 'absolute',
                      bottom: 48,
                      left: 8,
                      zIndex: 1000,
                      p: 1.5,
                      backgroundColor: alpha(theme.palette.background.paper, 0.95),
                      backdropFilter: 'blur(8px)',
                      minWidth: 200,
                      maxWidth: 280,
                    }}
                  >
                    {/* Header with collapse button */}
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                      <Typography variant="caption" fontWeight="bold">
                        Map Info
                      </Typography>
                      <IconButton 
                        size="small" 
                        onClick={() => setInfoPanelOpen(false)}
                        sx={{ p: 0.25 }}
                      >
                        <ExpandLessIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                    
                    {/* Selection info placeholder */}
                    {selectedNodes.size > 0 && (
                      <Box sx={{ mb: 1, pb: 1, borderBottom: 1, borderColor: 'divider' }}>
                        <Typography variant="caption" color="primary.main" fontWeight="bold" display="block">
                          Selection Active
                        </Typography>
                        <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                          {selectedNodes.size} node{selectedNodes.size > 1 ? 's' : ''} selected in graph
                        </Typography>
                      </Box>
                    )}
                    
                    {/* Legend */}
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', mb: 0.5, display: 'block' }}>
                      CONNECTION TYPES
                    </Typography>
                    <Stack spacing={0.25}>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <CircleIcon sx={{ fontSize: 10, color: '#ef4444' }} />
                        <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Dwells</Typography>
                      </Stack>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <CircleIcon sx={{ fontSize: 10, color: '#3b82f6' }} />
                        <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Calls</Typography>
                      </Stack>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <CircleIcon sx={{ fontSize: 10, color: '#10b981' }} />
                        <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>SMS</Typography>
                      </Stack>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <CircleIcon sx={{ fontSize: 10, color: '#f59e0b' }} />
                        <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Browsing</Typography>
                      </Stack>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <CircleIcon sx={{ fontSize: 10, color: '#9ca3af' }} />
                        <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Clusters</Typography>
                      </Stack>
                    </Stack>
                  </Paper>
                )}
              </>
            )}

            {/* Flow Map Info Panel - Collapsible */}
            {flowMapEnabled && showLegend && (
              <>
                {/* Collapsed state - just show toggle button (auto-open if location selected) */}
                {!infoPanelOpen && !flowPopupInfo && (
                  <IconButton
                    onClick={() => setInfoPanelOpen(true)}
                    sx={{
                      position: 'absolute',
                      bottom: 48,
                      left: 8,
                      zIndex: 1000,
                      bgcolor: alpha(theme.palette.background.paper, 0.95),
                      backdropFilter: 'blur(8px)',
                      '&:hover': { bgcolor: alpha(theme.palette.background.paper, 1) },
                    }}
                    size="small"
                  >
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                )}
                
                {/* Expanded state OR location selected - show full info panel */}
                {(infoPanelOpen || flowPopupInfo) && (
                  <Paper
                    elevation={2}
                    sx={{
                      position: 'absolute',
                      bottom: 48,
                      left: 8,
                      zIndex: 1000,
                      p: 1.5,
                      backgroundColor: alpha(theme.palette.background.paper, 0.95),
                      backdropFilter: 'blur(8px)',
                      minWidth: 200,
                      maxWidth: 260,
                    }}
                  >
                    {/* Header with collapse button */}
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                      <Typography variant="caption" fontWeight="bold">
                        Dwell Flow Info
                      </Typography>
                      <IconButton 
                        size="small" 
                        onClick={() => {
                          setInfoPanelOpen(false);
                          if (flowPopupInfo) {
                            setSelectedFlowNode(null);
                            setFlowPopupInfo(null);
                            if (onNodeClick) onNodeClick('', []);
                          }
                        }}
                        sx={{ p: 0.25 }}
                      >
                        <ExpandLessIcon fontSize="small" />
                      </IconButton>
                    </Stack>

                    {/* Selected Location Info */}
                    {flowPopupInfo && (
                      <Box sx={{ pb: 1, mb: 1, borderBottom: 1, borderColor: 'divider' }}>
                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
                          <Typography variant="caption" fontWeight="bold" color="primary.main" noWrap sx={{ maxWidth: 140 }}>
                            {flowPopupInfo.node.label}
                          </Typography>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              px: 0.5, 
                              py: 0.1, 
                              borderRadius: 0.5, 
                              fontSize: '0.6rem',
                              bgcolor: flowPopupInfo.node.tier === 1 ? 'error.main' : flowPopupInfo.node.tier === 2 ? 'primary.main' : 'grey.500',
                              color: 'white',
                            }}
                          >
                            {flowPopupInfo.node.tier === 1 ? 'HUB' : flowPopupInfo.node.tier === 2 ? 'SEC' : 'OTH'}
                          </Typography>
                        </Stack>
                        <Stack spacing={0.25}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            Visits: <strong>{flowPopupInfo.node.visitCount}</strong> • Entities: <strong>{flowPopupInfo.node.entityCount}</strong> • Links: <strong>{flowPopupInfo.node.connectionCount}</strong>
                          </Typography>
                          {flowPopupInfo.node.entities.length > 0 && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                              {flowPopupInfo.node.entities.slice(0, 3).join(', ')}
                              {flowPopupInfo.node.entities.length > 3 && ` +${flowPopupInfo.node.entities.length - 3}`}
                            </Typography>
                          )}
                          <Typography variant="caption" color="info.main" sx={{ fontSize: '0.7rem' }}>
                            {dwellFlowData.edgeArray.filter(e => 
                              e.sourceId === flowPopupInfo.node.id || e.targetId === flowPopupInfo.node.id
                            ).length} connected locations
                          </Typography>
                          {onNodeClick && flowPopupInfo.node.entities.length > 0 && (
                            <Typography variant="caption" color="success.main" sx={{ fontStyle: 'italic', fontSize: '0.65rem' }}>
                              ✓ Graph filtered to entities at this location
                            </Typography>
                          )}
                        </Stack>
                      </Box>
                    )}

                    {/* Overview Stats */}
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', mb: 0.25, display: 'block' }}>
                        OVERVIEW
                      </Typography>
                      <Stack spacing={0.25}>
                        <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                          <strong style={{ color: '#ef4444' }}>{dwellFlowData.nodeArray.filter(n => n.tier === 1).length}</strong> hubs • 
                          <strong style={{ color: '#3b82f6' }}> {dwellFlowData.nodeArray.filter(n => n.tier === 2).length}</strong> secondary • 
                          <strong style={{ color: '#6b7280' }}> {dwellFlowData.nodeArray.filter(n => n.tier === 3).length}</strong> other
                        </Typography>
                        <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                          {dwellFlowData.edgeArray.length} transitions • {visibleNodeIds.size} visible
                        </Typography>
                        {expandedHubs.size > 0 && (
                          <Typography variant="caption" color="secondary.main" sx={{ fontSize: '0.65rem' }}>
                            {expandedHubs.size} hub{expandedHubs.size > 1 ? 's' : ''} expanded
                          </Typography>
                        )}
                      </Stack>
                    </Box>

                    {/* Legend Section */}
                    <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', mb: 0.25, display: 'block' }}>
                        LEGEND
                      </Typography>
                      <Stack spacing={0.25}>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <CircleIcon sx={{ fontSize: 12, color: '#ef4444' }} />
                          <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>Hub</Typography>
                        </Stack>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <CircleIcon sx={{ fontSize: 10, color: '#3b82f6' }} />
                          <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>Secondary</Typography>
                        </Stack>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <CircleIcon sx={{ fontSize: 8, color: '#6b7280' }} />
                          <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>Other</Typography>
                        </Stack>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Box sx={{ width: 14, height: 2, bgcolor: '#ef4444', borderRadius: 1, opacity: 0.7 }} />
                          <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>Flow line</Typography>
                        </Stack>
                      </Stack>
                    </Box>
                  </Paper>
                )}
              </>
            )}

            {/* Area Selection Tools */}
            <Paper
              elevation={2}
              sx={{
                position: 'absolute',
                top: 8,
                right: 100,
                zIndex: 1000,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                p: 0.5,
                backgroundColor: alpha(theme.palette.background.paper, 0.95),
                backdropFilter: 'blur(8px)',
              }}
            >
              <Tooltip title={isDrawing ? "Cancel Selection" : "Select Area to Filter"}>
                <IconButton 
                  size="small" 
                  onClick={toggleDrawMode}
                  sx={{ 
                    bgcolor: isDrawing ? 'warning.light' : 'transparent',
                    '&:hover': { bgcolor: isDrawing ? 'warning.main' : 'action.hover' }
                  }}
                >
                  <CropFreeIcon fontSize="small" sx={{ color: isDrawing ? '#ed6c02' : isDarkMode ? '#fff' : '#333' }} />
                </IconButton>
              </Tooltip>
              {selectedArea && (
                <Tooltip title="Clear Area Filter">
                  <IconButton size="small" onClick={clearAreaSelection}>
                    <ClearIcon fontSize="small" sx={{ color: '#d32f2f' }} />
                  </IconButton>
                </Tooltip>
              )}
            </Paper>

            {/* Area Selection Indicator */}
            {isDrawing && (
              <Paper
                elevation={3}
                sx={{
                  position: 'absolute',
                  bottom: 24,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  zIndex: 1000,
                  px: 2,
                  py: 1,
                  backgroundColor: isDarkMode ? alpha('#1a1a2e', 0.95) : alpha('#ffffff', 0.95),
                  backdropFilter: 'blur(8px)',
                  border: `1px solid ${isDarkMode ? '#f59e0b' : '#ed6c02'}`,
                  borderRadius: 2,
                }}
              >
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: isDarkMode ? '#f59e0b' : '#ed6c02',
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Box component="span" sx={{ fontSize: '1.1em' }}>⬚</Box>
                  Draw rectangle to filter area
                </Typography>
              </Paper>
            )}

            {/* Selection Rectangle Layer */}
            {selectionRectangle && (
              <Source id="selection-rect" type="geojson" data={selectionRectangle as any}>
                <Layer
                  id="selection-rect-fill"
                  type="fill"
                  paint={{
                    'fill-color': '#3b82f6',
                    'fill-opacity': 0.15,
                  }}
                />
                <Layer
                  id="selection-rect-outline"
                  type="line"
                  paint={{
                    'line-color': '#3b82f6',
                    'line-width': 2,
                    'line-dasharray': [2, 2],
                  }}
                />
              </Source>
            )}

            {/* ================================================================ */}
            {/* DWELL FLOW MAP LAYERS */}
            {/* ================================================================ */}
            {flowMapEnabled && flowEdgesGeoJSON.features.length > 0 && (
              <Source id="flow-edges" type="geojson" data={flowEdgesGeoJSON as any}>
                <Layer
                  id="flow-edges-layer"
                  type="line"
                  paint={{
                    'line-color': '#f59e0b', // Orange/Amber - matches dwell color in graph
                    'line-width': ['get', 'width'],
                    'line-opacity': ['get', 'opacity'],
                  }}
                  layout={{
                    'line-cap': 'round',
                    'line-join': 'round',
                  }}
                />
              </Source>
            )}

            {flowMapEnabled && flowNodesGeoJSON.features.length > 0 && (
              <Source 
                id="flow-nodes-source" 
                type="geojson" 
                data={flowNodesGeoJSON as any}
              >
                {/* Node circles - tier-based styling with selection highlighting */}
                <Layer
                  id="flow-nodes"
                  type="circle"
                  paint={{
                    'circle-color': [
                      'case',
                      ['get', 'isSelected'], '#fff', // White for clicked/selected node
                      ['get', 'isShared'], '#FFD700', // GOLD for shared locations (2+ entities)
                      ['get', 'isConnectedToSelected'], '#00CED1', // CYAN for single entity's locations
                      ['get', 'isExpanded'], '#7c3aed', // Purple for expanded hubs
                      ['==', ['get', 'tier'], 1], '#ef4444', // Red for hubs
                      ['==', ['get', 'tier'], 2], '#3b82f6', // Blue for secondary
                      '#6b7280' // Gray for tertiary
                    ],
                    'circle-radius': ['get', 'size'],
                    'circle-opacity': ['get', 'opacity'],
                    'circle-stroke-width': [
                      'case',
                      ['get', 'isSelected'], 2,
                      ['get', 'isShared'], 3, // Thicker stroke for shared
                      ['get', 'isConnectedToSelected'], 2.5, // Visible stroke for single entity
                      ['get', 'isExpanded'], 2,
                      ['get', 'isConnected'], 1.5,
                      ['==', ['get', 'tier'], 1], 1.5,
                      1
                    ],
                    'circle-stroke-color': [
                      'case',
                      ['get', 'isSelected'], '#ef4444', // Red for clicked
                      ['get', 'isShared'], '#FFD700', // Gold border for shared
                      ['get', 'isConnectedToSelected'], '#fff', // White border for single entity
                      ['get', 'isExpanded'], '#7c3aed',
                      ['get', 'isConnected'], '#ef4444',
                      '#fff'
                    ],
                  }}
                />
                {/* Node labels - show based on tier and zoom, prioritize selected/shared locations */}
                <Layer
                  id="flow-node-labels"
                  type="symbol"
                  layout={{
                    'text-field': ['get', 'label'],
                    'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                    'text-size': [
                      'case',
                      ['get', 'isShared'], 12, // Larger labels for shared locations
                      ['get', 'isConnectedToSelected'], 11, // Visible labels for single entity
                      ['==', ['get', 'tier'], 1], 11,
                      ['==', ['get', 'tier'], 2], 10,
                      9
                    ],
                    'text-offset': [0, 1.0],
                    'text-anchor': 'top',
                    'text-max-width': 12,
                  }}
                  paint={{
                    'text-color': [
                      'case',
                      ['get', 'isShared'], '#FFD700', // Gold text for shared
                      ['get', 'isConnectedToSelected'], '#00CED1', // Cyan text for single entity
                      isDarkMode ? '#fff' : '#1f2937'
                    ],
                    'text-halo-color': isDarkMode ? 'rgba(0,0,0,0.8)' : 'rgba(255,255,255,0.9)',
                    'text-halo-width': [
                      'case',
                      ['get', 'isShared'], 2,
                      ['get', 'isConnectedToSelected'], 1.5,
                      1
                    ],
                    'text-opacity': [
                      'case',
                      ['get', 'isShared'], 1, // Always show shared location labels
                      ['get', 'isConnectedToSelected'], 1, // Always show single entity labels
                      ['get', 'isSelected'], 1,
                      ['get', 'isExpanded'], 1,
                      ['get', 'isConnected'], 0.95,
                      ['==', ['get', 'tier'], 1], 0.9,
                      ['==', ['get', 'tier'], 2], currentZoom > 11 ? 0.8 : 0,
                      currentZoom > 13 ? 0.7 : 0
                    ],
                  }}
                />
                {/* Point count badge - shows when multiple coordinates are grouped (centered like clusters) */}
                <Layer
                  id="flow-node-count"
                  type="symbol"
                  filter={['>', ['get', 'pointCount'], 1]}
                  layout={{
                    'text-field': ['get', 'pointCountLabel'],
                    'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                    'text-size': 10,
                    'text-anchor': 'center',
                    'text-allow-overlap': true,
                  }}
                  paint={{
                    'text-color': '#ffffff',
                    'text-halo-color': '#000000',
                    'text-halo-width': 1,
                  }}
                />
              </Source>
            )}

            {/* Flow Node Popup removed - info now shown in legend panel */}

            {/* ================================================================ */}
            {/* STANDARD CLUSTER LAYERS (hidden when flow map is enabled) */}
            {/* ================================================================ */}
            {!flowMapEnabled && (
              <Source
                id="dwells"
                type="geojson"
                data={geojsonData as any}
                cluster={true}
                clusterMaxZoom={14}
                clusterRadius={50}
              >
                  {/* Cluster circles */}
                  <Layer
                    id="clusters"
                    type="circle"
                    source="dwells"
                    filter={['has', 'point_count']}
                    paint={{
                      'circle-color': clusterColor,
                      'circle-radius': [
                        'step',
                        ['get', 'point_count'],
                        18,
                        10,
                        25,
                        30,
                        35
                      ],
                      'circle-opacity': 0.8,
                      'circle-stroke-width': 3,
                      'circle-stroke-color': '#fff'
                    }}
                  />

                  {/* Cluster count labels */}
                  <Layer
                    id="cluster-count"
                    type="symbol"
                    source="dwells"
                    filter={['has', 'point_count']}
                    layout={{
                      'text-field': '{point_count_abbreviated}',
                      'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                      'text-size': 14
                    }}
                    paint={{
                      'text-color': '#ffffff',
                      'text-halo-color': '#000000',
                      'text-halo-width': 1
                    }}
                  />

                  {/* Individual unclustered points */}
                  <Layer
                    id="unclustered-point"
                    type="circle"
                    source="dwells"
                    filter={['!', ['has', 'point_count']]}
                    paint={{
                      'circle-color': [
                        'match',
                        ['get', 'type'],
                        'dwell', '#ef4444',
                        'call', '#3b82f6',
                        'sms', '#10b981',
                        'browsing', '#f59e0b',
                        'app', '#7c3aed',
                        '#9333ea'
                      ],
                      'circle-radius': 10,
                      'circle-opacity': 0.7,
                      'circle-stroke-width': 2,
                      'circle-stroke-color': '#fff'
                    }}
                  />
              </Source>
            )}
          </Map>
        )}
      </Box>
    </Box>
  );
};

// Wrap in React.memo with custom comparator to avoid circular reference comparison
const NetworkGraphMap = React.memo(NetworkGraphMapInner, arePropsEqual);

export default NetworkGraphMap;
